import type { EffectNode, Skill } from '../types.js';
import type { SourceSnapshot } from '../sim/source-profile.js';
import {
  validateCanonicalSkillV2,
  type CanonicalValidationIssue,
} from './contract.js';
import { resolveLegacyDeliveryForApplication } from './application.js';
import { contactBindings, markLeaves, statusLeaves, whileNames } from './composite-validate.js';
import type {
  CanonicalEffectNodeV2,
  CanonicalSkillV2,
  CanonicalSkillV2Input,
  SkillApplicationV2,
} from './types.js';

declare const runtimeSkillPlanBrand: unique symbol;

/**
 * The compiled execution shape, named for the role it plays here. It is the
 * canonical compiler's *output*, never authored input: `Skill` is structurally
 * shared by the authored V1 form (`LegacySkillV1`), the base the canonical
 * contract narrows (`CanonicalSkillV2Input`), and this. The nominal guarantee
 * that a value reached execution through the loader lives on
 * `RuntimeSkillPlan`'s brand — this alias names the role, it does not carry it.
 */
export type RuntimeSkill = Skill;

/** Runtime-only copy of a canonical contact application. The legacy Delivery
 * on `RuntimeSkill` is retained as an analytic/lifecycle compatibility view;
 * execution consults this metadata to resolve the actual equipped profile and
 * region fractions. It is never accepted as authored canonical input. */
export type RuntimeWeaponContactSkill = Skill & {
  readonly weaponContactApplication?: SkillApplicationV2;
};

/** A measurement view realized against a snapshot (`realizeSkillWithSnapshot`) whose element package still realizes at the cast and needs that snapshot there. */
export type RuntimeSourceSnapshotSkill = Skill & {
  readonly measurementSourceSnapshot?: SourceSnapshot;
};

export interface RuntimeSkillPlan {
  readonly canonicalSkill: CanonicalSkillV2;
  readonly runtimeSkill: RuntimeWeaponContactSkill;
  readonly [runtimeSkillPlanBrand]: true;
}

export type CompileResult =
  | { readonly status: 'COMPILED'; readonly plan: RuntimeSkillPlan }
  | { readonly status: 'INVALID'; readonly issues: readonly CanonicalValidationIssue[] }
  | { readonly status: 'DEFERRED'; readonly code: string; readonly input: CanonicalSkillV2Input };

function invalid(path: string, code: string, message: string): { readonly status: 'INVALID'; readonly issues: readonly CanonicalValidationIssue[] } {
  return { status: 'INVALID', issues: [{ path, code, message }] };
}

function deferred(code: string, input: CanonicalSkillV2Input): CompileResult {
  return { status: 'DEFERRED', code, input };
}

/**
 * The legacy runtime has a deliberately small policy surface.  Do not drop a
 * richer V2 contact/lifecycle policy while lowering it to `Delivery`: a
 * request that cannot be represented exactly remains DEFERRED.
 */
function unsupportedRuntimePolicy(application: SkillApplicationV2, effects: readonly CanonicalEffectNodeV2[] = []): string | undefined {
  const policy = application.contactPolicy;
  if (policy.hitPolicy !== 'ONCE_PER_TARGET' || policy.termination !== 'FIRST_VALID_CONTACT') return 'CONTACT_POLICY_RUNTIME_UNIMPLEMENTED';
  if (policy.perTargetCooldownS !== undefined || policy.contactLimit !== undefined) return 'CONTACT_POLICY_RUNTIME_UNIMPLEMENTED';
  // `maxPierce` is the bodies one attack may strike: a projectile passes through
  // them and a weapon contact strikes the nearest ones. Falloff scales the k-th
  // body a projectile passes through; no other motion has that order to scale.
  if (application.motion.kind !== 'PROJECTILE' && application.geometry.owner !== 'WEAPON_CONTACT' && policy.maxPierce !== undefined) return 'CONTACT_POLICY_RUNTIME_UNIMPLEMENTED';
  if (application.motion.kind !== 'PROJECTILE' && policy.pierceFalloff !== undefined) return 'CONTACT_POLICY_RUNTIME_UNIMPLEMENTED';

  const lifecycle = application.lifecycle;
  if (lifecycle.kind === 'PERSISTENT') {
    if (application.geometry.owner !== 'SKILL_OWNED' || application.geometry.shape.kind !== 'CIRCLE') return 'PERSISTENT_GEOMETRY_RUNTIME_UNIMPLEMENTED';
    if (lifecycle.mode === 'PULSE' && lifecycle.hitIntervalS === undefined) return 'PERSISTENT_PULSE_INTERVAL_REQUIRED';
    if (lifecycle.mode === 'CONTINUOUS' && lifecycle.hitIntervalS !== undefined) return 'PERSISTENT_CONTINUOUS_INTERVAL_UNIMPLEMENTED';
  }
  if (lifecycle.kind === 'SUSTAINED') {
    // Axis L reuses the channel loop, whose pulse re-applies at a skill-owned
    // circle (the same geometry constraint PERSISTENT already carries).
    if (application.geometry.owner !== 'SKILL_OWNED' || application.geometry.shape.kind !== 'CIRCLE') return 'SUSTAINED_GEOMETRY_RUNTIME_UNIMPLEMENTED';
    if (!(lifecycle.maxSustainS > 0)) return 'SUSTAINED_MAX_DURATION_REQUIRED';
    if (!(lifecycle.hitIntervalS > 0)) return 'SUSTAINED_PULSE_INTERVAL_REQUIRED';
  }
  if (application.geometry.owner === 'SKILL_OWNED' && (application.geometry.shape.kind === 'CIRCLE' || application.geometry.shape.kind === 'SECTOR')
    && application.acquisitionRangeM !== undefined) return 'ACQUISITION_RANGE_RUNTIME_UNIMPLEMENTED';
  if (application.motion.kind === 'PROJECTILE' && application.acquisitionRangeM !== undefined
    && application.acquisitionRangeM !== application.motion.maxTravelM) return 'PROJECTILE_ACQUISITION_RANGE_MISMATCH';
  if (application.motion.kind === 'PROJECTILE' && application.motion.sourceLifetimePolicy !== undefined
    && application.motion.sourceLifetimePolicy !== 'CONTINUE_LAST_DIRECTION') return 'PROJECTILE_SOURCE_LIFETIME_UNIMPLEMENTED';
  for (const effect of effects) {
    if (effect.kind === 'secondary_attack') {
      // Secondary spatial sources need their own activation/measurement identity.
      if (effect.application.geometry.owner === 'WEAPON_CONTACT') return 'SECONDARY_WEAPON_CONTACT_SOURCE_UNIMPLEMENTED';
      const nestedApplication = unsupportedRuntimePolicy(effect.application, effect.effects);
      if (nestedApplication !== undefined) return nestedApplication;
    } else if (effect.kind === 'condition' || effect.kind === 'trigger') {
      if (effect.kind === 'trigger' && effect.event === 'on_expire') return 'TRIGGER_ON_EXPIRE_RUNTIME_UNIMPLEMENTED';
      // `on_projectile_expire` is raised by the projectile bridge only. On any
      // other motion the runtime never fires it, so a skill carrying one would
      // be priced for a follow-up that can never execute.
      if (effect.kind === 'trigger' && effect.event === 'on_projectile_expire' && application.motion.kind !== 'PROJECTILE') {
        return 'TRIGGER_ON_PROJECTILE_EXPIRE_REQUIRES_PROJECTILE';
      }
      const nested = unsupportedRuntimePolicy(application, effect.children);
      if (nested !== undefined) return nested;
    }
  }
  return undefined;
}

function toLegacyEffect(effect: CanonicalEffectNodeV2): EffectNode | { status: 'INVALID'; issues: readonly CanonicalValidationIssue[] } {
  if (effect.kind === 'condition' || effect.kind === 'trigger') {
    const children: EffectNode[] = [];
    for (const child of effect.children) {
      const converted = toLegacyEffect(child);
      if ('status' in converted) return converted;
      children.push(converted);
    }
    return { ...effect, children } as EffectNode;
  }
  if (effect.kind === 'secondary_attack') {
    const resolved = resolveLegacyDeliveryForApplication(effect.application);
    if (!resolved.ok) return invalid(resolved.path, resolved.code, resolved.message);
    const effects: EffectNode[] = [];
    for (const child of effect.effects) {
      const converted = toLegacyEffect(child);
      if ('status' in converted) return converted;
      effects.push(converted);
    }
    return { kind: 'secondary_attack', delivery: resolved.delivery, procPolicy: effect.procPolicy, effects,
      ...(effect.application.geometry.owner === 'WEAPON_CONTACT' ? { weaponContactApplication: effect.application } : {}) } as EffectNode;
  }
  return JSON.parse(JSON.stringify(effect)) as EffectNode;
}

/**
 * Lower a canonical effect subtree for runtime-only metadata such as a
 * WEAPON_CONTACT binding.  Canonical children are readonly and secondary
 * applications carry V2 geometry rather than a legacy Delivery, so a direct
 * cast here would lose nested contact semantics.  The same lowering used by
 * the root skill is deliberately reused and invalid subtrees fail loudly at
 * the compiler boundary.
 */
export function lowerCanonicalEffectNodes(nodes: readonly CanonicalEffectNodeV2[]): EffectNode[] {
  const lowered: EffectNode[] = [];
  for (const node of nodes) {
    const converted = toLegacyEffect(node);
    if ('status' in converted) {
      throw new Error(converted.issues[0]?.message ?? 'canonical effect subtree cannot be lowered');
    }
    lowered.push(converted);
  }
  return lowered;
}

/**
 * Compiles only validated, currently executable V2 data to the existing runtime plan.
 *
 * A `mark` is consumed only by a composite cast's detonation stage, so a skill
 * that is not one of a composite plan's stages (`compositeStage`) may not carry
 * one: nothing would ever detonate or clear it.
 */
export function compileCanonicalSkill(input: CanonicalSkillV2Input, options: { readonly compositeStage?: boolean } = {}): CompileResult {
  const validation = validateCanonicalSkillV2(input);
  if (!validation.ok) {
    if (validation.status === 'DEFERRED') return { status: 'DEFERRED', code: validation.code, input };
    return { status: 'INVALID', issues: validation.issues };
  }
  if (!validation.ok) throw new Error('unreachable');
  const canonical = validation.skill;
  if (options.compositeStage !== true && carriesMark(canonical)) {
    return { status: 'INVALID', issues: [{ path: 'effects', code: 'COMPOSITE_MARK_NEVER_CONSUMED', message: 'a mark is consumed only by a composite cast\'s detonation stage; a single skill would leave it forever' }] };
  }
  if (options.compositeStage !== true && namesSkillStatus(canonical)) {
    return { status: 'INVALID', issues: [{ path: 'effects', code: 'SKILL_STATUS_UNKNOWN', message: 'a skill status is defined by a composite skill; a single skill defines none, so nothing could be applied or bound to' }] };
  }
  if (canonical.application.geometry.owner === 'WEAPON_CONTACT' && canonical.sourceRequirement !== undefined && canonical.sourceRequirement.sourceClass !== 'WEAPON') return deferred('WEAPON_CONTACT_SOURCE_CLASS_UNSUPPORTED', input);
  const policyDeferral = unsupportedRuntimePolicy(canonical.application, canonical.effects);
  if (policyDeferral !== undefined) return deferred(policyDeferral, input);
  const resolved = resolveLegacyDeliveryForApplication(canonical.application);
  if (!resolved.ok) return deferred(resolved.code, input);
  const effects: EffectNode[] = [];
  for (const effect of canonical.effects) {
    const converted = toLegacyEffect(effect);
    if ('status' in converted) return deferred(converted.issues[0]?.code ?? 'SECONDARY_APPLICATION_UNIMPLEMENTED', input);
    effects.push(converted);
  }
  const { schemaVersion: _schemaVersion, application: _application, effects: _effects, ...legacyFields } = canonical;
  const lifecycle = canonical.application.lifecycle;
  const sustain = lifecycle.kind === 'SUSTAINED'
    ? { maxSustainS: lifecycle.maxSustainS, hitIntervalS: lifecycle.hitIntervalS, end: lifecycle.end }
    : undefined;
  return { status: 'COMPILED', plan: { canonicalSkill: canonical, runtimeSkill: {
    ...legacyFields,
    delivery: resolved.delivery,
    ...(sustain ? { sustain } : {}),
    effects,
    ...(canonical.application.geometry.owner === 'WEAPON_CONTACT' ? { weaponContactApplication: canonical.application } : {}),
  } } as unknown as RuntimeSkillPlan };
}

function namesSkillStatus(skill: CanonicalSkillV2Input): boolean {
  const inEffects = (effects: unknown): boolean => { const found = statusLeaves(effects); return found.top.length > 0 || found.nested || whileNames(effects).length > 0; };
  return inEffects(skill.effects) || contactBindings({ application: skill.application }).some((binding) => inEffects(binding.effects));
}

function carriesMark(skill: CanonicalSkillV2Input): boolean {
  const inEffects = (effects: unknown): boolean => { const found = markLeaves(effects); return found.top.length > 0 || found.nested; };
  return inEffects(skill.effects) || contactBindings({ application: skill.application }).some((binding) => inEffects(binding.effects));
}
